import { track } from "@/lib/track";
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { GoogleSignInButton } from "@/components/google-sign-in-button";
import { AppleSignInButton } from "@/components/apple-sign-in-button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/use-auth";
import { validateInviteCode, consumeInviteCode } from "@/lib/invite.functions";
import { Eye, EyeOff, Ticket } from "lucide-react";

type Props = { open: boolean; onOpenChange: (o: boolean) => void };

export function SignInDialog({ open, onOpenChange }: Props) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="px-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)]">
          <DrawerHeader className="text-center sm:text-center">
            <DrawerTitle className="text-xl font-semibold tracking-tight">Welcome to Pyng</DrawerTitle>
            <DrawerDescription>Sign in to see today's people.</DrawerDescription>
          </DrawerHeader>
          <SignInForm onSuccess={() => onOpenChange(false)} />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-semibold tracking-tight">Welcome to Pyng</DialogTitle>
          <DialogDescription className="text-center">Sign in to see today's people.</DialogDescription>
        </DialogHeader>
        <SignInForm onSuccess={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

function SignInForm({ onSuccess }: { onSuccess: () => void }) {
  const { confirmAuth } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const validateCode = useServerFn(validateInviteCode);
  const consumeCode = useServerFn(consumeInviteCode);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<"signin" | "signup">("signin");

  const onGoogleTokens = async () => {
    const user = await confirmAuth();
    setBusy(false);
    if (!user) {
      toast.error("Google sign-in completed, but no session was created");
      return;
    }
    sessionStorage.removeItem("orbit:post-auth-path");
    onSuccess();
    await navigate({ to: "/today", replace: true });
  };

  const onGoogleError = (message: string) => {
    sessionStorage.removeItem("orbit:post-auth-path");
    setBusy(false);
    toast.error(message);
  };

  const onAppleTokens = async () => {
    const user = await confirmAuth();
    setBusy(false);
    if (!user) {
      toast.error("Apple sign-in completed, but no session was created");
      return;
    }
    sessionStorage.removeItem("orbit:post-auth-path");
    onSuccess();
    await navigate({ to: "/today", replace: true });
  };

  const onAppleError = (message: string) => {
    sessionStorage.removeItem("orbit:post-auth-path");
    setBusy(false);
    toast.error(message);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);

    if (mode === "signup") {
      if (!inviteCode.trim()) {
        toast.error("An invite code is required to create an account");
        setBusy(false);
        return;
      }
      const check = await validateCode({ data: { code: inviteCode.trim() } });
      if (!check.valid) {
        toast.error(check.reason ?? "That invite code isn't valid");
        setBusy(false);
        return;
      }
    }

    const { error } =
      mode === "signin"
        ? await supabase.auth.signInWithPassword({ email, password })
        : await supabase.auth.signUp({
            email,
            password,
            options: { emailRedirectTo: window.location.origin + "/today" },
          });
    if (error) {
      toast.error(error.message);
      setBusy(false);
      return;
    }
    const user = await confirmAuth();
    if (!user) {
      setBusy(false);
      onSuccess();
      toast.error(
        mode === "signin"
          ? "Sign-in completed, but no session was created"
          : "Account created, but no session was started. Try signing in.",
      );
      return;
    }

    if (mode === "signup" && inviteCode.trim()) {
      try {
        const res: any = await consumeCode({ data: { code: inviteCode.trim() } });
        if (!res?.ok) {
          toast.error(res?.reason ?? "That invite code couldn't be redeemed");
          await qc.invalidateQueries();
          setBusy(false);
          onSuccess();
          await navigate({ to: "/welcome", replace: true });
          return;
        }
      } catch (err: any) {
        toast.error(err?.message ?? "Your account was created, but the code didn't redeem");
        await qc.invalidateQueries();
        setBusy(false);
        onSuccess();
        await navigate({ to: "/welcome", replace: true });
        return;
      }
      sessionStorage.removeItem("pyng:invite-code");
    }

    if (mode === "signup") {
      track("signup", { source: "sign_in_dialog", method: "password" });
      toast.success("Welcome to Pyng.");
    }
    await qc.invalidateQueries();
    setBusy(false);
    onSuccess();
    await navigate({ to: "/today", replace: true });
  };

  return (
    <div className="px-1 pb-1">
      <div className="space-y-2.5">
        <GoogleSignInButton
          busy={busy}
          onStart={() => {
            setBusy(true);
            sessionStorage.setItem("orbit:post-auth-path", "/today");
          }}
          onTokens={onGoogleTokens}
          onError={onGoogleError}
        />
        <AppleSignInButton
          busy={busy}
          onStart={() => {
            setBusy(true);
            sessionStorage.setItem("orbit:post-auth-path", "/today");
          }}
          onTokens={onAppleTokens}
          onError={onAppleError}
        />
      </div>

      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
      </div>

      <form onSubmit={submit} className="space-y-3">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="h-11 w-full rounded-xl border border-border bg-card px-4 text-sm outline-none placeholder:text-muted-foreground"
        />
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="h-11 w-full rounded-xl border border-border bg-card px-4 pr-11 text-sm outline-none placeholder:text-muted-foreground"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {mode === "signup" && (
          <div className="relative animate-in-fade">
            <Ticket className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              placeholder="Invite code"
              className="h-11 w-full rounded-xl border border-border bg-card px-4 pl-10 text-sm uppercase tracking-wider outline-none placeholder:text-muted-foreground placeholder:normal-case placeholder:tracking-normal"
              autoComplete="off"
            />
          </div>
        )}
        <button
          disabled={busy}
          className="h-11 w-full rounded-xl bg-primary text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-px disabled:opacity-60"
        >
          {mode === "signin" ? "Sign in" : "Create account"}
        </button>
      </form>
      <button
        type="button"
        onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
        className="mt-4 w-full text-center text-xs text-muted-foreground hover:text-foreground"
      >
        {mode === "signin" ? "Need an account? Sign up" : "Already have an account? Sign in"}
      </button>
    </div>
  );
}
